// Amadeus extra-bags + flexible-bags + bag parsing. Each function is
// exposed as a method on AmadeusProvider and dispatched from
// providers/index.js — consumers import the dispatchers, not these helpers.

import Store from '@/vuex/store'
import { deep_clone } from '@/utilities/'
import { is_infant } from '@liveflight/utilities/travelers'
import { get_offer_details } from './upstream'

/**
 * Convert a `flexible_bag` selection into a canonical
 * FareDetailAdditionalService array entry. Provider-specific extras
 * stay on `_raw`.
 *
 * @param {Object} flexible_bag
 * @returns {Object}
 */
const flexible_bag_to_entry = (flexible_bag) => {
  const { id = '', quantity, weight, weightUnit = '', name = '' } = flexible_bag || {};
  const entry = { type: 'bag', _raw: flexible_bag };
  if (id) entry.id = id;
  if (quantity !== undefined) entry.quantity = Number(quantity);
  if (weight !== undefined) entry.weight = Number(weight);
  if (weightUnit) entry.weightUnit = weightUnit;
  if (name) entry.name = name;
  return entry;
};

/**
 * Mutate the RAW Amadeus offer in place: wipe every non-infant
 * fareDetailsBySegment.additionalServices Object, then re-apply each
 * added flexible bag as `additionalServices.chargeableCheckedBags`.
 * Stays in Amadeus's native Object shape so the body sent to
 * flightOffersPrice carries the wire contract Amadeus expects.
 *
 * @param {Object} target
 * @param {Object} flexible_bags
 * @returns {boolean} true when at least one flexible bag was applied
 */
const apply_flexible_bags_to_raw = (target, flexible_bags) => {
  let has_flexible_selection = false;
  target.travelerPricings.forEach((traveler) => {
    traveler.fareDetailsBySegment.forEach((fare_detail_by_segment) => {
      if (is_infant(traveler)) return;
      fare_detail_by_segment.additionalServices = {};
    });
  });
  Object.keys(flexible_bags).forEach((t) => {
    let traveler = flexible_bags[t];
    Object.keys(traveler).forEach((i) => {
      let itinerary = traveler[i];
      Object.keys(itinerary).forEach((b) => {
        if (!itinerary[b].added) return;
        let flexible_bag = itinerary[b].bag;
        has_flexible_selection = true;
        let traveler_priced = target.travelerPricings[t];
        traveler_priced.fareDetailsBySegment.forEach((s) => {
          if (!flexible_bag.segmentIds.includes(s.segmentId)) return;
          if (is_infant(traveler_priced)) return;
          if (
            target.itineraries[i].segments.filter((segment) => {
              return segment.id == s.segmentId;
            }).length > 0
          )
            s.additionalServices.chargeableCheckedBags = flexible_bag;
        });
      });
    });
  });
  return has_flexible_selection;
};

/**
 * Mutate the CANONICAL normalized offer in place: wipe every non-infant
 * fareDetailsBySegment[].additionalServices to [], then push one
 * `{type:'bag', ...}` entry per added flexible bag. Matches the array
 * shape declared in providers/types.js.
 *
 * @param {Object} target
 * @param {Object} flexible_bags
 * @returns {boolean} true when at least one flexible bag was applied
 */
const apply_flexible_bags_to_canonical = (target, flexible_bags) => {
  let has_flexible_selection = false;
  target.travelerPricings.forEach((traveler) => {
    traveler.fareDetailsBySegment.forEach((fare_detail_by_segment) => {
      if (is_infant(traveler)) return;
      fare_detail_by_segment.additionalServices = [];
    });
  });
  Object.keys(flexible_bags).forEach((t) => {
    let traveler = flexible_bags[t];
    Object.keys(traveler).forEach((i) => {
      let itinerary = traveler[i];
      Object.keys(itinerary).forEach((b) => {
        if (!itinerary[b].added) return;
        let flexible_bag = itinerary[b].bag;
        has_flexible_selection = true;
        let traveler_priced = target.travelerPricings[t];
        if (is_infant(traveler_priced)) return;
        const entry = flexible_bag_to_entry(flexible_bag);
        traveler_priced.fareDetailsBySegment.forEach((s) => {
          if (!flexible_bag.segmentIds.includes(s.segmentId)) return;
          if (
            target.itineraries[i].segments.filter((segment) => {
              return segment.id == s.segmentId;
            }).length > 0
          )
            s.additionalServices.push({ ...entry });
        });
      });
    });
  });
  return has_flexible_selection;
};

export function add_charged_bags(flight_offer, flexible_bags) {
  // Deep clone to ensure a new reference is committed to Vuex,
  // so Vue 2's reactivity reliably detects the change. The clone also
  // strips Object.freeze from _raw so we can mirror the selection there.
  const updated_offer = deep_clone(flight_offer);
  const has_flexible_selection = apply_flexible_bags_to_canonical(updated_offer, flexible_bags);

  // Mirror the mutation onto _raw so the next flightOffersPrice reprice
  // (dispatched by the flight_offer watcher in detail.vue) actually asks
  // Amadeus to price these bags. The raw stays in Amadeus's native
  // Object shape so the body matches the wire contract.
  if (updated_offer._raw) {
    apply_flexible_bags_to_raw(updated_offer._raw, flexible_bags);
  }

  // Drop only the CHECKED_BAGS roll-up — the upcoming reprice
  // (include=['bags']) will repopulate it. Other extras (SEATS, …) live
  // through their own reprice paths and must survive this update.
  const { additionalServices: previous_additional_services = [] } = updated_offer.price || {};
  const kept = Array.isArray(previous_additional_services)
    ? previous_additional_services.filter((s) => s && s.type !== 'CHECKED_BAGS')
    : [];
  updated_offer.price.additionalServices = kept;
  Store.commit('liveflight/has_flexible_bags_last_selection', has_flexible_selection);
  return updated_offer;
}

/**
 * Async variant: stages the bag selection onto the offer + _raw (via
 * add_charged_bags) and then routes through get_offer_details →
 * flightOffersPrice (include=['bags']) to reprice. The catalog cache on
 * the provider is refreshed in the same call so a subsequent
 * get_extra_bags reads the post-application catalog.
 *
 * @returns {Promise<Object>} repriced normalized offer
 */
export const add_extra_bags = (provider, flight_offer, flexible_bags) => {
  const staged_offer = add_charged_bags(flight_offer, flexible_bags);
  return get_offer_details(provider, staged_offer);
};

export function has_chargeable_bags(flight_offer, traveler_id) {
  let has_bags = false;
  flight_offer.travelerPricings
    .filter((t) => {
      return t.travelerId == traveler_id;
    })[0]
    .fareDetailsBySegment.forEach((fdbs) => {
      const { additionalServices = [] } = fdbs || {};
      const list = Array.isArray(additionalServices) ? additionalServices : [];
      list.forEach((entry) => {
        const { type = '', quantity = 0 } = entry || {};
        if (type === 'bag' && Number(quantity) > 0) {
          has_bags = true;
        }
      });
    });
  return has_bags;
}

export function has_included_bags(flight_offer, traveler_id) {
  const pricing = flight_offer.travelerPricings.find((t) => t.travelerId == traveler_id);
  if (!pricing) return false;
  return pricing.fareDetailsBySegment.some((fdbs) => {
    const { includedCheckedBags = {} } = fdbs || {};
    return includedCheckedBags.quantity > 0 || includedCheckedBags.weight > 0;
  });
}

const bag_quantity = (bag) => {
  if (bag.quantity) {
    return bag.quantity;
  } else if (bag.weight) {
    return 1;
  }
  return 0;
};

export function get_bags(flight_offer, itinerary_index = 0) {
  const parsed = {
    extra: false,
    travelers: [],
    cabin: 0,
    hand: 0,
    included: 0,
    chargeable: 0,
    total: 0,
  };

  const { travelerPricings = [], itineraries = [] } = flight_offer || {};
  if (itinerary_index < 0 || itinerary_index >= itineraries.length) {
    return parsed;
  }

  const { segments: itinerary_segments = [] } = itineraries[itinerary_index];
  if (itinerary_segments.length === 0) {
    return parsed;
  }
  const first_segment_of_current_itinerary = itinerary_segments[0].id;

  travelerPricings.forEach((traveler_pricing) => {
    const fare_details_by_segment = (traveler_pricing.fareDetailsBySegment || []).filter(
      (f) => f.segmentId === first_segment_of_current_itinerary
    )[0];
    const traveler = {
      traveler_id: traveler_pricing.travelerId,
      traveler_type: traveler_pricing.traveler_type || traveler_pricing.travelerType,
      cabin: 0,
      hand: 0,
      included: 0,
      chargeable: 0,
    };

    if (fare_details_by_segment) {
      if (fare_details_by_segment.includedCheckedBags) {
        traveler.included = bag_quantity(fare_details_by_segment.includedCheckedBags);
        parsed.included += traveler.included;
      }

      if (fare_details_by_segment.includedCabinBags) {
        traveler.cabin = bag_quantity(fare_details_by_segment.includedCabinBags);
        parsed.cabin += traveler.cabin;
      }

      // includedHandBags (personal item) — Amadeus heuristic puts 1 per
      // non-infant in normalize, Kyte fills it from baggageAllowance.
      if (fare_details_by_segment.includedHandBags) {
        traveler.hand = bag_quantity(fare_details_by_segment.includedHandBags);
        parsed.hand += traveler.hand;
      }

      const { additionalServices = [] } = fare_details_by_segment;
      const seg_list = Array.isArray(additionalServices) ? additionalServices : [];
      const bag_entries = seg_list.filter((entry) => entry && entry.type === 'bag');
      if (bag_entries.length) {
        traveler.chargeable = bag_entries.reduce(
          (carry, entry) => carry + bag_quantity(entry),
          0,
        );
        parsed.chargeable += traveler.chargeable;
      }
    }

    // Hide extras when bags are already attached to the segment.
    if (flight_offer.price) {
      const { additionalServices: seg_additional = [] } = fare_details_by_segment || {};
      const has_bag_for_segment =
        Array.isArray(seg_additional) &&
        seg_additional.some((entry) => entry && entry.type === 'bag');
      if (has_bag_for_segment) {
        parsed.extra = false;
      } else if (flight_offer.price.additionalServices) {
        const additional_services = flight_offer.price.additionalServices.filter(
          (s) => s.type === 'CHECKED_BAGS'
        );
        if (additional_services.length) {
          parsed.extra = true;
        }
      }
    }

    parsed.travelers.push(traveler);
  });

  return {
    ...parsed,
    total: parsed.included + parsed.chargeable,
  };
}
